import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldAlert, KeyRound, UserCheck } from "lucide-react";

export default function AdminLogin() {
  const { loginAdmin, isAuthenticated, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);

  // Redirect if already logged in as Admin
  React.useEffect(() => {
    if (isAuthenticated && isAdmin) {
      navigate("/admin/orders");
    }
  }, [isAuthenticated, isAdmin, navigate]);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.username || !form.password) {
      return toast.error("Please fill in both username and password.");
    }

    setLoading(false);
    setLoading(true);
    try {
      await loginAdmin(form.username, form.password);
      toast.success("Authorized successfully. Accessing seller portal...");
      navigate("/admin/orders");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid seller credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[85vh] px-6 md:px-12 lg:px-20 py-24 flex items-center justify-center bg-[var(--ayu-bg)]">
      <div className="max-w-md w-full bg-white border border-[var(--ayu-border)] p-10 text-center relative overflow-hidden grain shadow-sm">
        <div className="absolute top-0 left-0 right-0 h-1 bg-[var(--ayu-secondary)]"></div>

        <div className="w-12 h-12 bg-[#FAF8F5] text-[var(--ayu-secondary)] flex items-center justify-center mx-auto mb-6 border border-[var(--ayu-border)]">
          <KeyRound size={20} strokeWidth={1.5} />
        </div>

        <p className="overline mb-3">Staff Portal</p>
        <h1 className="font-display text-4xl mb-8 text-[var(--ayu-heading)]">Seller Authentication</h1>

        <form onSubmit={onSubmit} className="space-y-6 text-left">
          <div>
            <label className="overline text-[10px] block mb-1.5" style={{ color: "var(--ayu-muted)" }}>
              Seller Identity / Username
            </label>
            <input
              type="text"
              name="username"
              data-testid="admin-username-input"
              value={form.username}
              onChange={onChange}
              required
              className="ayu-input py-2 text-sm"
              placeholder="e.g. admin"
            />
          </div>

          <div>
            <label className="overline text-[10px] block mb-1.5" style={{ color: "var(--ayu-muted)" }}>
              Access Password
            </label>
            <input
              type="password"
              name="password"
              data-testid="admin-password-input"
              value={form.password}
              onChange={onChange}
              required
              className="ayu-input py-2 text-sm"
              placeholder="••••••••"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="btn-accent w-full justify-center text-sm py-3 mt-4 disabled:opacity-60 transition-opacity"
          >
            {loading ? "Decrypting ledger…" : "Authenticate & Open Portal"}
          </button>
        </form>

        <div className="mt-8 pt-6 border-t border-[var(--ayu-border)] flex items-center gap-2 justify-center text-xs opacity-75" style={{ color: "var(--ayu-muted)" }}>
          <ShieldAlert size={14} className="text-[var(--ayu-secondary)]" />
          <span>Restricted to authorized clinical personnel only.</span>
        </div>
      </div>
    </div>
  );
}
